import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useTheme } from '@/lib/ThemeContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Menu, 
  X, 
  User, 
  LogOut, 
  LayoutDashboard,
  MapPin,
  Search,
  FileText,
  CheckSquare,
  Sparkles,
  ChevronDown,
  Sun,
  Moon
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_globalcampus-2/artifacts/g96w8w8c_HE%20Direct%20logo%202023.png";

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getDashboardLink = () => {
    if (user?.role === 'student') return '/student';
    if (user?.role === 'agent') return '/agent';
    if (user?.role === 'admin') return '/admin';
    return '/dashboard';
  };

  const getApplicationsLink = () => {
    if (isAuthenticated && (user?.role === 'admin' || user?.role === 'employee')) {
      return '/admin?tab=applications';
    }
    if (isAuthenticated && user?.role === 'agent') {
      return '/agent?tab=applications';
    }
    return '/applications';
  };

  const navLinks = [
    { href: '/courses', label: 'Courses', icon: Search },
    { href: '/destinations', label: 'Destinations', icon: MapPin },
    { href: '/check-eligibility', label: 'Check Eligibility', icon: CheckSquare },
    { href: '/visa-probability', label: 'Visa Probability', icon: Sparkles },
    { href: getApplicationsLink(), label: 'Applications', icon: FileText },
  ];

  const isActive = (path) => {
    if (path.includes('?')) {
      const [pathname, search] = path.split('?');
      return location.pathname === pathname && location.search.includes(search);
    }
    return location.pathname === path;
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#f8f9fa]/95 dark:bg-slate-900/95 backdrop-blur-xl border-b border-gray-200/60 dark:border-slate-700/60 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="navbar-logo">
            <img 
              src={LOGO_URL} 
              alt="H E Direct" 
              className="h-14 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-medium transition-colors relative py-2 ${
                  isActive(link.href) 
                    ? 'text-teal-700 dark:text-teal-400' 
                    : 'text-gray-600 dark:text-gray-300 hover:text-teal-700 dark:hover:text-teal-400'
                }`}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
                {isActive(link.href) && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-600 dark:bg-teal-400 rounded-full"></span>
                )}
              </Link>
            ))}
          </div>

          {/* Desktop Auth & Theme Toggle */}
          <div className="hidden md:flex items-center gap-3">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2.5 rounded-full bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors"
              data-testid="theme-toggle"
              aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
            >
              {theme === 'light' ? (
                <Moon className="w-5 h-5" />
              ) : (
                <Sun className="w-5 h-5" />
              )}
            </button>

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="flex items-center gap-2 text-gray-600 hover:text-teal-700 hover:bg-gray-100"
                    data-testid="user-menu-trigger"
                  >
                    <div className="w-9 h-9 rounded-full bg-teal-600 flex items-center justify-center">
                      <User className="w-4 h-4 text-gray-800" />
                    </div>
                    <span className="font-medium">{user?.full_name?.split(' ')[0]}</span>
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                  align="end" 
                  className="w-56 bg-white border border-gray-200 shadow-lg"
                >
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-semibold text-gray-800">{user?.full_name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{user?.email}</p>
                    <span className="inline-block mt-2 px-2 py-0.5 text-xs font-semibold uppercase bg-teal-100 text-teal-700 rounded">
                      {user?.role}
                    </span>
                  </div>
                  <div className="py-1">
                    <DropdownMenuItem 
                      onClick={() => navigate(getDashboardLink())}
                      className="text-gray-600 hover:text-teal-700 hover:bg-[#f8f9fa] cursor-pointer px-4 py-2"
                      data-testid="nav-dashboard"
                    >
                      <LayoutDashboard className="w-4 h-4 mr-3" />
                      Dashboard
                    </DropdownMenuItem>
                  </div>
                  <DropdownMenuSeparator className="bg-gray-100" />
                  <div className="py-1">
                    <DropdownMenuItem 
                      onClick={handleLogout}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 cursor-pointer px-4 py-2"
                      data-testid="nav-logout"
                    >
                      <LogOut className="w-4 h-4 mr-3" />
                      Logout
                    </DropdownMenuItem>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login">
                  <Button 
                    variant="ghost" 
                    className="text-gray-600 hover:text-teal-700 hover:bg-gray-100 font-medium"
                    data-testid="nav-login"
                  >
                    Login
                  </Button>
                </Link>
                <Link to="/register">
                  <Button 
                    className="bg-teal-600 hover:bg-teal-700 text-white font-semibold shadow-sm rounded-full px-6"
                    data-testid="nav-register"
                  >
                    Get Started
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu button */}
          <div className="md:hidden flex items-center gap-2">
            {/* Mobile Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors"
              data-testid="mobile-theme-toggle"
              aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
            >
              {theme === 'light' ? (
                <Moon className="w-5 h-5" />
              ) : (
                <Sun className="w-5 h-5" />
              )}
            </button>
            {!isAuthenticated && (
              <Link to="/login">
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="text-gray-600 dark:text-gray-300 hover:text-teal-700 dark:hover:text-teal-400 font-medium"
                  data-testid="mobile-login"
                >
                  Login
                </Button>
              </Link>
            )}
            <button
              className="p-2 text-gray-600 dark:text-gray-300 hover:text-teal-700 dark:hover:text-teal-400 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-[#f8f9fa] border-t border-gray-200">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`flex items-center gap-3 py-3 px-4 text-base font-medium rounded-xl transition-colors ${
                  isActive(link.href) 
                    ? 'text-teal-700 bg-teal-50' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <link.icon className="w-5 h-5" />
                {link.label}
              </Link>
            ))}
            
            <div className="pt-4 mt-4 border-t border-gray-200 space-y-2">
              {isAuthenticated ? (
                <>
                  <div className="px-4 py-3 bg-gray-100 rounded-xl mb-3">
                    <p className="text-sm font-semibold text-gray-800">{user?.full_name}</p>
                    <p className="text-xs text-gray-500">{user?.email}</p>
                  </div>
                  <Link
                    to={getDashboardLink()}
                    className="flex items-center gap-3 py-3 px-4 text-base font-medium text-gray-600 rounded-xl hover:bg-gray-100"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <LayoutDashboard className="w-5 h-5" />
                    Dashboard
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-3 py-3 px-4 w-full text-base font-medium text-red-600 rounded-xl hover:bg-red-50"
                  >
                    <LogOut className="w-5 h-5" />
                    Logout
                  </button>
                </>
              ) : (
                <div className="space-y-3 pt-2">
                  <Link
                    to="/register"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Button className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold h-12 text-base rounded-xl">
                      Get Started Free
                    </Button>
                  </Link>
                  <Link
                    to="/agent-register"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Button variant="outline" className="w-full border-2 border-gray-200 text-gray-600 h-12 text-base font-medium rounded-xl hover:bg-[#f8f9fa]">
                      Register as Agent
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
